/**
 * Payroll — yearly report (owners/admins). Everything paid in a year:
 * gross, net, CPP, EI, federal/provincial tax, deductions, vacation pay,
 * hours — company-wide and per employee — with ‹ › to step through years,
 * stub counts, blank-stub counts and the weeks that have no stub at all.
 * Numbers come from the same source as each employee's profile YTD (app
 * paystubs + imported Knit stubs, never double-counted).
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download, FileSpreadsheet, FileText, FileUp, Loader2 } from "lucide-react";
import api from "@/lib/api";
import T4PrepView from "./T4PrepView";
import { downloadBlob } from "./downloadBlob";
import VacationOwedDialog from "./VacationOwedDialog";

/** Jump to Payroll with the "Import pay stubs" popup open (optionally naming the employee). */
const useOpenImport = () => {
  const [, setParams] = useSearchParams();
  return (forName) => setParams((prev) => { const p = new URLSearchParams(prev); p.set("tab", "payroll"); p.set("import", "1"); if (forName) p.set("for", forName); else p.delete("for"); return p; });
};

const money = (v) => `$${Number(v || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hrs = (v) => `${Number(v || 0).toLocaleString("en-CA", { maximumFractionDigits: 2 })} h`;

const COLS = [
  ["gross", "Gross"], ["net", "Net paid"], ["cpp", "CPP"], ["ei", "EI"],
  ["federal_tax", "Federal tax"], ["provincial_tax", "ON tax"],
  ["total_deductions", "Deductions"], ["vacation_pay", "Vac earned"], ["vacation_banked", "Vac banked"],
  ["employer_cpp", "Employer CPP"], ["employer_ei", "Employer EI"], ["employer_cost", "True cost"],
];

const StatCard = ({ label, value, sub, accent, testId }) => (
  <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={testId}>
    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50">{label}</p>
    <p className={`mt-1 font-mono text-lg font-bold tabular-nums ${accent || "text-white"}`}>{value}</p>
    {sub && <div className="mt-0.5 font-mono text-[10px] text-white/50">{sub}</div>}
  </div>
);

export default function PayrollYearReport() {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openGaps, setOpenGaps] = useState(null); // employee_id whose missing weeks are expanded
  const openImport = useOpenImport();
  const [owedOpen, setOwedOpen] = useState(false);
  const [zipping, setZipping] = useState(null);
  const [view, setView] = useState("summary");    // "summary" | "t4"
  const [quarter, setQuarter] = useState(0);      // 0 = whole year, 1-4 = quarter
  const [exporting, setExporting] = useState(null); // "csv" | "pdf" while downloading

  const load = useCallback(async (yr, q) => {
    setLoading(true);
    try {
      const r = await api.get("/payroll/year-report", { params: { year: yr, ...(q ? { quarter: q } : {}) } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the payroll report");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(year, quarter); }, [load, year, quarter]);

  const exportZip = async (row) => {
    setZipping(row.employee_id);
    try {
      const r = await api.get(`/payroll/imported-paystubs/${row.employee_id}/export`, { params: { year }, responseType: "blob" });
      downloadBlob(r.data, `${row.name.replace(/[^\w\- ]+/g, "_").replace(/\s+/g, "_")}_paystubs_${year}.zip`, r.headers["content-disposition"]);
    } catch {
      toast.error(`No imported stubs for ${row.name} in ${year}`);
    } finally {
      setZipping(null);
    }
  };

  const exportReport = async (format) => {
    setExporting(format);
    try {
      const r = await api.get("/payroll/year-report-export", { params: { year, format, ...(quarter ? { quarter } : {}) }, responseType: "blob" });
      downloadBlob(r.data, `payroll_report_${year}${quarter ? `_Q${quarter}` : ""}.${format}`, r.headers["content-disposition"]);
      toast.success(`Payroll report ${year}${quarter ? ` Q${quarter}` : ""} downloaded as ${format.toUpperCase()}`);
    } catch {
      toast.error("Could not export the report");
    } finally {
      setExporting(null);
    }
  };

  const g = data?.grand || {};
  const years = data?.years || [year];
  const canPrev = year > (years[0] ?? year) - 1;
  const canNext = year < new Date().getFullYear();

  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="payroll-year-report">
      {/* header + year nav */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Payroll · yearly report</p>
          <h2 className="mt-1 text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
            Paid in {year}{quarter && view === "summary" ? ` · Q${quarter}` : ""}{(!quarter || view === "t4") && year === new Date().getFullYear() ? " · to date" : ""}
          </h2>
          <p className="mt-1 font-mono text-[10px] text-white/50">
            App paystubs + imported stubs, never counted twice. Blank $0 stubs count as "on file"; a missing week has no stub at all.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-lg border border-white/10 bg-white/[0.02] p-1" role="tablist" data-testid="payroll-report-view">
            {[["summary", "Summary"], ["t4", "T4 prep"]].map(([k, lb]) => (
              <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => setView(k)} data-testid={`payroll-report-view-${k}`}
                className={`rounded-md px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${view === k ? "bg-[#1a3fd1] text-white shadow" : "text-white/70 hover:text-white"}`}>
                {lb}
              </button>
            ))}
          </div>
          {view === "summary" && (
            <div className="flex items-center rounded-lg border border-white/10 bg-white/[0.02] p-1" role="tablist" data-testid="payroll-report-quarter">
              {[[0, "Year"], [1, "Q1"], [2, "Q2"], [3, "Q3"], [4, "Q4"]].map(([q, lb]) => (
                <button key={q} type="button" role="tab" aria-selected={quarter === q} onClick={() => setQuarter(q)} data-testid={`payroll-report-quarter-${q}`}
                  title={q ? `${["Jan–Mar", "Apr–Jun", "Jul–Sep", "Oct–Dec"][q - 1]} ${year}` : `All of ${year}`}
                  className={`rounded-md px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest ${quarter === q ? "bg-[#1a3fd1] text-white shadow" : "text-white/70 hover:text-white"}`}>
                  {lb}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.02] p-1" data-testid="payroll-report-export">
            <button type="button" onClick={() => exportReport("csv")} disabled={!!exporting} data-testid="payroll-report-export-csv"
              title="Download this year's report as a spreadsheet for the accountant"
              className="inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-white/70 hover:border-[#1a3fd1] hover:text-white disabled:opacity-50">
              {exporting === "csv" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />} CSV
            </button>
            <button type="button" onClick={() => exportReport("pdf")} disabled={!!exporting} data-testid="payroll-report-export-pdf"
              title="Download this year's report as a PDF"
              className="inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-white/70 hover:border-[#1a3fd1] hover:text-white disabled:opacity-50">
              {exporting === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} PDF
            </button>
            <button type="button" onClick={() => openImport()} data-testid="payroll-report-import-stubs"
              title="Import pay stubs (PDF or CSV) — opens the import window on Payroll"
              className="inline-flex items-center gap-1.5 rounded-md border border-[#1a3fd1]/60 bg-[#1a3fd1]/15 px-2.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-white hover:bg-[#1a3fd1]/30">
              <FileUp className="h-3.5 w-3.5" /> Import stubs
            </button>
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.02] p-1" data-testid="payroll-report-year-nav">
            <button type="button" onClick={() => setYear((y) => y - 1)} disabled={!canPrev} aria-label="Previous year" data-testid="payroll-report-prev"
              className="rounded-md border border-white/10 p-1.5 text-white/70 hover:border-[#1a3fd1] hover:text-white disabled:opacity-30">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[4.5rem] text-center font-mono text-sm font-bold text-white" data-testid="payroll-report-year">{year}</span>
            <button type="button" onClick={() => setYear((y) => y + 1)} disabled={!canNext} aria-label="Next year" data-testid="payroll-report-next"
              className="rounded-md border border-white/10 p-1.5 text-white/70 hover:border-[#1a3fd1] hover:text-white disabled:opacity-30">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {view === "t4" ? <T4PrepView year={year} /> : loading && !data ? (
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/50" data-testid="payroll-report-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </p>
      ) : data && (
        <>
          {/* company-wide */}
          <div className={`grid grid-cols-2 gap-2 sm:grid-cols-3 ${loading ? "opacity-60" : ""}`} data-testid="payroll-report-grand">
            <StatCard label="Total payroll (gross)" value={money(g.gross)} sub={`${g.employees_paid || 0} employee${g.employees_paid === 1 ? "" : "s"} paid · ${g.stubs || 0} stubs`} testId="payroll-report-gross" />
            <StatCard label="Net paid out" value={money(g.net)} accent="text-emerald-300" testId="payroll-report-net" />
            <StatCard label="CPP" value={money(g.cpp)} testId="payroll-report-cpp" />
            <StatCard label="EI" value={money(g.ei)} testId="payroll-report-ei" />
            <StatCard label="Federal tax" value={money(g.federal_tax)} testId="payroll-report-fed" />
            <StatCard label="Ontario tax" value={money(g.provincial_tax)} testId="payroll-report-prov" />
            <StatCard label="Total deductions" value={money(g.total_deductions)} sub="CPP + EI + taxes + other" testId="payroll-report-deductions" />
            <StatCard label="Vacation pay earned" value={money(g.vacation_pay)} testId="payroll-report-vacation"
              sub={<>
                <span>accrued on {year} pay (4% / 6%) · {money(g.vacation_paid_out)} paid out on cheques</span>
                <button type="button" onClick={() => setOwedOpen(true)} data-testid="payroll-report-vacation-banked"
                  className={`mt-0.5 block text-left font-bold underline-offset-2 hover:underline ${g.vacation_banked > 0 ? "text-red-400" : "text-emerald-300"}`}>
                  {g.vacation_banked > 0 ? `${money(g.vacation_banked)} still banked (owed to employees) — who?` : "Nothing banked — all paid out"}
                </button>
              </>} />
            <StatCard label="Hours paid" value={hrs(g.hours_worked)} testId="payroll-report-hours" />
          </div>

          {/* employer side — what payroll really costs the company */}
          <div className={`grid grid-cols-2 gap-2 sm:grid-cols-3 ${loading ? "opacity-60" : ""}`} data-testid="payroll-report-employer">
            <StatCard label="Employer CPP" value={money(g.employer_cpp)} sub="matches the employee 1:1" testId="payroll-report-employer-cpp" />
            <StatCard label="Employer EI" value={money(g.employer_ei)} sub="1.4x the employee premium" testId="payroll-report-employer-ei" />
            <StatCard label="True cost of payroll" value={money(g.employer_cost)} accent="text-[#1a3fd1] dark:text-[#8fa8ff]"
              sub="gross + employer CPP + employer EI" testId="payroll-report-employer-cost" />
          </div>

          {/* per employee */}
          <div className="overflow-x-auto rounded-lg border border-white/10 bg-white/[0.02]" data-testid="payroll-report-table">
            <table className="w-full min-w-[1400px] border-collapse text-xs">
              <thead>
                <tr className="font-mono text-[9px] uppercase tracking-widest text-white/50">
                  <th className="px-3 py-2 text-left font-normal">Employee</th>
                  {COLS.map(([k, lb]) => <th key={k} className="px-2 py-2 text-right font-normal">{lb}</th>)}
                  <th className="px-2 py-2 text-right font-normal">Hours</th>
                  <th className="px-2 py-2 text-right font-normal">Stubs</th>
                  <th className="px-2 py-2 text-right font-normal">Gaps</th>
                  <th className="px-2 py-2 text-right font-normal"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {data.employees.map((r) => (
                  <RowGroup key={r.employee_id} row={r} open={openGaps === r.employee_id} onImport={() => openImport(r.name)}
                    onToggle={() => setOpenGaps((v) => (v === r.employee_id ? null : r.employee_id))}
                    onZip={() => exportZip(r)} zipping={zipping === r.employee_id} />
                ))}
                {data.employees.length === 0 && (
                  <tr><td colSpan={COLS.length + 5} className="px-3 py-6 text-center font-mono text-[11px] text-white/50">Nothing paid in {year}.</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t border-white/20 font-bold text-white" data-testid="payroll-report-total-row">
                  <td className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Company total</td>
                  {COLS.map(([k]) => <td key={k} className={`px-2 py-2 text-right font-mono tabular-nums ${k === "vacation_banked" && g[k] > 0 ? "text-red-400" : ""}`}>{money(g[k])}</td>)}
                  <td className="px-2 py-2 text-right font-mono tabular-nums">{hrs(g.hours_worked)}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums">{g.stubs || 0}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums">{g.missing_weeks || 0}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
      <VacationOwedDialog open={owedOpen} onClose={() => setOwedOpen(false)} rows={data?.employees || []} total={g?.vacation_banked || 0} />
    </div>
  );
}

const RowGroup = ({ row, open, onToggle, onZip, zipping, onImport }) => {
  const t = row.totals || {};
  const gaps = row.missing?.length || 0;
  return (
    <>
      <tr className="text-white/80 hover:bg-white/[0.03]" data-testid={`payroll-report-row-${row.employee_id}`}>
        <td className="px-3 py-1.5">
          <span className="font-semibold text-white">{row.name}</span>
          {row.status !== "Active" && (
            <span className="ml-1.5 rounded-full bg-white/15 px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-wider text-white/80">{row.status}</span>
          )}
          {row.blank_stubs > 0 && (
            <span className="ml-1.5 rounded-full bg-[#5a4208] px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-wider text-[#fde68a]">{row.blank_stubs} blank</span>
          )}
        </td>
        {COLS.map(([k]) => <td key={k} className={`px-2 py-1.5 text-right font-mono tabular-nums ${k === "vacation_banked" && t[k] > 0 ? "text-red-400" : ""}`}>{money(t[k])}</td>)}
        <td className="px-2 py-1.5 text-right font-mono tabular-nums">{hrs(t.hours_worked)}</td>
        <td className="px-2 py-1.5 text-right font-mono tabular-nums" title={`${row.app_stubs} app · ${row.imported_stubs} imported`}>{t.stubs || 0}</td>
        <td className="px-2 py-1.5 text-right font-mono tabular-nums">
          {row.no_data ? (
            <span className="text-white/40" title="No stubs on file this year">—</span>
          ) : gaps === 0 ? (
            <span className="text-emerald-300">0</span>
          ) : (
            <button type="button" onClick={onToggle} data-testid={`payroll-report-gaps-${row.employee_id}`}
              className="rounded bg-[#5a4208] px-1.5 py-0.5 font-bold text-[#fde68a] underline-offset-2 hover:underline">
              {gaps} {open ? "▴" : "▾"}
            </button>
          )}
        </td>
        <td className="px-2 py-1.5 text-right">
          <button type="button" onClick={onZip} disabled={!row.imported_stubs || zipping}
            data-testid={`payroll-report-zip-${row.employee_id}`}
            title={row.imported_stubs ? `Download all ${row.imported_stubs} imported stubs (originals) as a zip` : "No imported stubs this year"}
            className="inline-flex items-center gap-1 rounded border border-white/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white/70 hover:border-[#1a3fd1] hover:text-white disabled:cursor-not-allowed disabled:opacity-30">
            {zipping ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} zip
          </button>
        </td>
      </tr>
      {open && gaps > 0 && (
        <tr data-testid={`payroll-report-gaps-list-${row.employee_id}`}>
          <td colSpan={COLS.length + 5} className="bg-white/[0.02] px-3 py-2">
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/50">
              Weeks with no stub for {row.name}{row.expected != null ? ` · ${row.covered} of ${row.expected} on file` : " (this quarter)"}
            </p>
            <div className="mt-1 flex flex-wrap gap-1">
              {row.missing.map((m) => (
                <button key={m} type="button" onClick={onImport} title={`Import the stub for the week of ${m}`} data-testid={`payroll-report-missing-${row.employee_id}-${m}`}
                  className="rounded bg-[#5a4208] px-1.5 py-0.5 font-mono text-[10px] text-[#fde68a] hover:bg-[#7a5a0c] hover:underline">{m}</button>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
